// client/src/components/CRMPipeline.jsx — Phase B3
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ContractReviewModal from './ContractReviewModal';
import { Plus, RefreshCw, Search, ChevronDown, ChevronUp, ArrowUpRight, ChevronRight } from 'lucide-react';
import { api } from '../api';
import { useUser } from '../context/UserContext';
import { fmtCurrency } from '../utils';
import T from './crmTheme';
import CompanyDirectory from './CRMCompanyDirectory';
import ContactDirectory from './CRMContactDirectory';
import CRMInboundForm from './CRMInboundForm';
import InboxView from './CRMInboxView';


// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const STAGES = [
  { id: 'new',        label: 'New',        color: '#3b82f6' },
  { id: 'qualified',  label: 'Qualified',  color: '#7c3aed' },
  { id: 'bid_review', label: 'Bid Review', color: '#d97706' },
  { id: 'bidding',    label: 'Bidding',    color: '#0e9aaa' },
  { id: 'won',        label: 'Won',        color: '#059669' },
];

const W3W_REGEX = /^[a-z]+\.[a-z]+\.[a-z]+$/;
// SUR Phase 1: W3W stays construction-only; address extends to surveying.
function isW3WRequired(form) {
  return form.project_type === 'construction';
}
function isAddressRequired(form) {
  return form.project_type === 'construction' || form.project_type === 'surveying';
}

const SOURCE_LABELS = {
  cold_outreach: 'Cold Outreach', referral: 'Referral', framework: 'Framework',
  repeat_client: 'Repeat Client', event: 'Event / Exhibition', inbound: 'Inbound', other: 'Other',
};

const INT_STYLES = {
  call:       { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6',  label: 'CALL' },
  email:      { bg: 'rgba(5,150,105,0.12)',   color: '#10b981',  label: 'EMAIL' },
  meeting:    { bg: 'rgba(139,92,246,0.12)',  color: '#8b5cf6',  label: 'MTG' },
  note:       { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8',  label: 'NOTE' },
  site_visit: { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b',  label: 'SITE' },
  other:      { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8',  label: 'OTHER' },
};


// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function fmtVal(pence) {
  if (!pence) return '—';
  const v = Number(pence) / 100;
  if (v >= 1000000) return `£${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `£${Math.round(v / 1000)}k`;
  return `£${Math.round(v)}`;
}

function fmtValFull(pence) {
  if (!pence) return '—';
  return fmtCurrency(Number(pence) / 100);
}

function bubbleSize(pence) {
  const v = Number(pence) / 100;
  if (!v) return 14;
  if (v < 50000) return 16;
  if (v < 200000) return 26;
  if (v < 500000) return 36;
  return 46;
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function shortDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}


// ═══════════════════════════════════════════════════════════════════════════════
// THEME — CSS variable driven, respects parent [data-theme] or prefers-color-scheme
// ═══════════════════════════════════════════════════════════════════════════════

// Theme tokens — use global CSS variables from index.css


// ═══════════════════════════════════════════════════════════════════════════════
// TOOLTIP
// ═══════════════════════════════════════════════════════════════════════════════

function Tooltip({ lead, pos }) {
  if (!lead || !pos) return null;
  const rows = [
    ['Est. Value', fmtValFull(lead.estimated_value_pence)],
    ['Source', SOURCE_LABELS[lead.source] || lead.source],
    lead.what3words ? ['W3W', '///' + lead.what3words] : null,
    lead.site_address ? ['Site', lead.site_address.split('\n')[0]] : null,
    ['Contact', lead.contact?.name || '—'],
    ['Logged', shortDate(lead.created_at)],
  ];
  return (
    <div style={{
      position: 'fixed', left: Math.min(pos.x + 16, window.innerWidth - 280),
      top: Math.min(pos.y + 16, window.innerHeight - 210),
      width: 260, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10,
      padding: 14, zIndex: 2000, pointerEvents: 'none', boxShadow: T.shadowHover,
    }}>
      <p style={{ color: T.text, fontWeight: 600, fontSize: 13, margin: '0 0 3px' }}>{lead.title}</p>
      <p style={{ color: T.textMuted, fontSize: 11, margin: '0 0 10px' }}>{lead.company?.name}</p>
      {rows.filter(Boolean).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: `1px solid ${T.borderLight}` }}>
          <span style={{ color: T.textMuted }}>{k}</span>
          <span style={{ color: T.text, fontFamily: "'DM Mono', monospace" }}>{v}</span>
        </div>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// LEAD CARD
// ═══════════════════════════════════════════════════════════════════════════════

function SiteAddressFields({ lead, onRefresh, showW3W = true }) {
  const [siteAddr, setSiteAddr] = useState(lead.site_address || '');
  const [w3w, setW3w] = useState(lead.what3words || '');
  useEffect(() => { setSiteAddr(lead.site_address || ''); }, [lead.site_address]);
  useEffect(() => { setW3w(lead.what3words || ''); }, [lead.what3words]);
  const [saving, setSaving] = useState(false);
  const W3W_RE = /^[a-z]+\.[a-z]+\.[a-z]+$/;

  async function saveSiteAddr() {
    const val = siteAddr.trim();
    if (val === (lead.site_address || '').trim()) return;
    setSaving(true);
    try {
      await api.crmUpdateLead(lead.id, { site_address: val || null });
      onRefresh();
    } catch (err) { alert(err.message || 'Failed to save site address'); }
    setSaving(false);
  }

  async function saveW3w() {
    const val = w3w.toLowerCase().replace(/[^a-z.]/g, '');
    setW3w(val);
    if (val === (lead.what3words || '')) return;
    if (val && !W3W_RE.test(val)) { alert('Format: word.word.word (lowercase)'); return; }
    setSaving(true);
    try {
      await api.crmUpdateLead(lead.id, { what3words: val || null });
      onRefresh();
    } catch (err) { alert(err.message || 'Failed to save What3Words'); }
    setSaving(false);
  }

  return (
    <div style={{ paddingLeft: 6, marginBottom: 6 }} onClick={e => e.stopPropagation()}>
      <label style={{ fontSize: 10, color: lead.site_address ? T.textMuted : '#d97706', fontWeight: 600, display: 'block', marginBottom: 2 }}>
        Site Address{!lead.site_address && ' *'}
      </label>
      <textarea
        value={siteAddr}
        rows={2}
        placeholder="Full site address..."
        onClick={e => e.stopPropagation()}
        onChange={e => { e.stopPropagation(); setSiteAddr(e.target.value); }}
        onBlur={saveSiteAddr}
        style={{
          width: '100%', padding: '3px 6px', fontSize: 10, resize: 'vertical',
          background: T.inputBg, border: `1px solid ${lead.site_address ? T.border : 'rgba(217,119,6,0.4)'}`,
          borderRadius: 4, color: T.text, fontFamily: 'inherit',
        }}
      />
      {showW3W && (<>
        <label style={{ fontSize: 10, color: lead.what3words ? T.textMuted : '#d97706', fontWeight: 600, display: 'block', margin: '4px 0 2px' }}>
          What3Words{!lead.what3words && ' *'}
        </label>
        <input
          value={w3w}
          placeholder="word.word.word"
          onClick={e => e.stopPropagation()}
          onChange={e => { e.stopPropagation(); setW3w(e.target.value.toLowerCase().replace(/[^a-z.]/g, '')); }}
          onBlur={saveW3w}
          style={{
            width: '100%', padding: '3px 6px', fontSize: 10,
            background: T.inputBg,
            border: `1px solid ${w3w && W3W_RE.test(w3w) ? T.teal : w3w ? '#ef4444' : 'rgba(217,119,6,0.4)'}`,
            borderRadius: 4, color: T.text, fontFamily: "'DM Mono', monospace",
          }}
        />
        {!lead.what3words && <span style={{ fontSize: 9, color: '#d97706', fontStyle: 'italic' }}>Required before Bid Review</span>}
      </>)}
      {saving && <span style={{ fontSize: 9, color: T.teal, fontStyle: 'italic' }}> Saving…</span>}
    </div>
  );
}

// ── InteractionModal ─────────────────────────────────────────────────────────
function InteractionModal({ lead, interactions, noteText, setNoteText, logging, onLog, onClose, T }) {
  const INT_STYLES = {
    call:    { bg: 'rgba(59,130,246,0.15)',  color: '#60a5fa', label: 'CALL' },
    email:   { bg: 'rgba(16,185,129,0.15)',  color: '#34d399', label: 'EMAIL' },
    meeting: { bg: 'rgba(139,92,246,0.15)',  color: '#a78bfa', label: 'MEETING' },
    note:    { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af', label: 'NOTE' },
    other:   { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af', label: 'OTHER' },
  };
  // Close on Escape
  React.useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'ipe-backdrop-in 0.18s ease forwards',
      }}>
      <style>{`
        @keyframes ipe-backdrop-in { from { opacity:0 } to { opacity:1 } }
        @keyframes ipe-modal-in { from { opacity:0; transform:scale(0.93) translateY(8px) } to { opacity:1; transform:scale(1) translateY(0) } }
        @keyframes ipe-modal-out { from { opacity:1; transform:scale(1) } to { opacity:0; transform:scale(0.93) translateY(8px) } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--modal-bg)', border: `1px solid ${T.border}`,
          borderRadius: 14, width: 480, maxWidth: '92vw',
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          animation: 'ipe-modal-in 0.2s cubic-bezier(0.34,1.56,0.64,1) forwards',
        }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{lead.title}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{lead.company?.name || ''} {lead.enq_ref ? `· ${lead.enq_ref}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: T.textDim, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}>✕</button>
        </div>
        {/* Interaction history */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 18px' }}>
          {interactions.length === 0 ? (
            <div style={{ color: T.textDim, fontSize: 12, fontStyle: 'italic', padding: '12px 0', textAlign: 'center' }}>No interactions yet</div>
          ) : (
            interactions.map((a, i) => {
              const s = INT_STYLES[a.interaction_type] || INT_STYLES.other;
              return (
                <div key={a.id || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 0', borderBottom: i < interactions.length - 1 ? `1px solid ${T.borderLight}` : 'none' }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: s.bg, color: s.color, flexShrink: 0, marginTop: 2, letterSpacing: '0.04em' }}>{s.label}</span>
                  <span style={{ color: T.textMuted, flex: 1, lineHeight: 1.5, fontSize: 12 }}>{a.summary || '—'}</span>
                  <span style={{ color: T.textDim, fontFamily: "'DM Mono', monospace", fontSize: 10, flexShrink: 0, marginTop: 2 }}>{new Date(a.interaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
                </div>
              );
            })
          )}
        </div>
        {/* Input area */}
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <textarea
            autoFocus
            placeholder="Log a note, call, email, or meeting…"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={5}
            style={{
              width: '100%', background: T.inputBg, border: `1px solid ${T.teal}`,
              borderRadius: 7, color: T.text, fontSize: 12, padding: '8px 10px',
              resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
              outline: 'none', boxShadow: `0 0 0 3px ${T.tealDim}`, lineHeight: 1.5,
            }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {['note', 'call', 'email', 'meeting'].map(t => (
              <button key={t} onClick={() => onLog(t)} disabled={logging || !noteText.trim()}
                style={{
                  padding: '5px 12px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${T.border}`, background: T.surface2, color: T.textMuted,
                  opacity: noteText.trim() ? 1 : 0.4, transition: 'all 0.15s',
                }}>
                + {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
            <button onClick={onClose}
              style={{
                marginLeft: 'auto', padding: '5px 14px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                cursor: 'pointer', border: `1px solid ${T.teal}`, background: T.tealDim, color: T.teal,
              }}>
              ✓ Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, stage, user, onRefresh, onHover, onLeave, onCardClick, onCRClick, onTenderCreated, isExpanded, onToggleExpand }) {
  const expanded = isExpanded;
  const [deciding, setDeciding] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [interactionExpanded, setInteractionExpanded] = useState(false);
  const [logging, setLogging] = useState(false);
  const [detail, setDetail] = useState(null);

  const bSize = bubbleSize(lead.estimated_value_pence);
  const daysIn = daysSince(lead.updated_at);
  const isReview = stage.id === 'bid_review';
  const canDecide = ['manager', 'director'].includes(user?.role);
  const canAdvance = ['bd', 'manager', 'director'].includes(user?.role) && ['new', 'qualified'].includes(lead.stage);

  async function loadDetail() {
    if (detail) return;
    try {
      const d = await api.crmLead(lead.id);
      setDetail(d);
    } catch (e) { console.error('Failed to load detail:', e); }
  }

  async function handleExpand() {
    if (!isExpanded) loadDetail();
    onToggleExpand();
  }

  async function handleDecision(decision) {
    setDeciding(true);
    try {
      await api.crmLeadDecision(lead.id, { decision, notes: noteText || undefined });
      onRefresh();
      if (decision === 'bid' && onTenderCreated) onTenderCreated();
    } catch (e) { alert(e.message || 'Decision failed'); }
    setDeciding(false);
  }

  async function handleAdvance() {
    try {
      await api.crmAdvanceLead(lead.id);
      onRefresh();
    } catch (e) { alert(e.message || 'Cannot advance'); }
  }

  async function handleLog(type) {
    if (!noteText.trim()) return;
    setLogging(true);
    try {
      await api.crmLogInteraction(lead.id, { type, note: noteText });
      setNoteText('');
      setDetail(null); // force reload
      onRefresh();
    } catch (e) { alert(e.message || 'Failed to log'); }
    setLogging(false);
  }

  const interactions = detail?.interactions || [];

  return (
    <div
      draggable={canAdvance || ['manager','director'].includes(user?.role)}
      onDragStart={e => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ leadId: lead.id, fromStage: stage.id }));
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.style.opacity = '0.4';
      }}
      onDragEnd={e => { e.currentTarget.style.opacity = stage.id === 'won' ? '0.72' : '1'; }}
      onMouseEnter={e => onHover(lead, { x: e.clientX, y: e.clientY })}
      onMouseMove={e => onHover(lead, { x: e.clientX, y: e.clientY })}
      onMouseLeave={onLeave}
      onClick={() => onCardClick && onCardClick(lead)}
      style={{
        background: isReview ? 'rgba(217,119,6,0.03)' : T.cardBg,
        border: `1px solid ${isReview ? 'rgba(217,119,6,0.25)' : T.cardBorder}`,
        borderRadius: 9, padding: 12, position: 'relative', overflow: 'visible',
        opacity: stage.id === 'won' ? 0.72 : 1, transition: 'all 0.18s',
        boxShadow: T.shadow, cursor: 'pointer',
      }}
    >
      {/* Left accent bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: stage.color, borderRadius: '9px 0 0 9px' }} />

      {/* Top: bubble + title */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 8, paddingLeft: 6 }}>
        <div style={{
          width: bSize, height: bSize, borderRadius: '50%', flexShrink: 0,
          background: stage.color + '18', border: `2px solid ${stage.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: stage.color, fontSize: 8, fontWeight: 700, textAlign: 'center', lineHeight: 1.1, fontFamily: "'DM Mono', monospace",
        }}>
          {fmtVal(lead.estimated_value_pence)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: T.text, fontSize: 13, fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {lead.title}
          </div>
          <div style={{ color: T.textMuted, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {lead.company?.name || '—'}
          </div>
        </div>
      </div>

      {/* Tags */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', paddingLeft: 6, marginBottom: 7 }}>
        {lead.source && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, background: 'rgba(59,130,246,0.08)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.2)' }}>
            {SOURCE_LABELS[lead.source] || lead.source}
          </span>
        )}
        {lead.what3words && (
          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 500, background: 'rgba(5,150,105,0.08)', color: '#059669', border: '1px solid rgba(5,150,105,0.2)', fontFamily: "'DM Mono', monospace" }}>
            ///{lead.what3words}
          </span>
        )}
        {lead.enq_ref && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 500, background: T.tealDim, color: T.teal, border: `1px solid ${T.tealGlow}`, fontFamily: "'DM Mono', monospace" }}>
            {lead.tender_ref || lead.enq_ref}
          </span>
        )}
        {lead.converted_opportunity_id && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, background: T.tealDim, color: T.teal, border: `1px solid ${T.tealGlow}`, display: 'flex', alignItems: 'center', gap: 3 }}>
            <ArrowUpRight size={10} /> In Dashboard
          </span>
        )}
        {lead.tender_status === 'won' && lead.contract_review_status !== 'complete' && (
          <button onClick={(e) => { e.stopPropagation(); onCRClick && onCRClick(lead); }}
            style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, background: 'rgba(217,119,6,0.1)', color: '#d97706', border: '1px solid rgba(217,119,6,0.3)', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontFamily: 'inherit' }}>
            ⏳ Won — CR Pending
          </button>
        )}
        {lead.stage === 'won' && lead.contract_review_status === 'complete' && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 600, background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.3)', display: 'flex', alignItems: 'center', gap: 3 }}>
            🏆 Won{lead.tender_won_value ? ` — £${(Number(lead.tender_won_value)/100).toLocaleString('en-GB', {maximumFractionDigits:0})}` : ''}
          </span>
        )}
      </div>

      {/* Stats badges */}
      <div style={{ display: 'flex', gap: 6, paddingLeft: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: T.textMuted, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 4, padding: '2px 7px' }}>
          💬 {lead.interaction_count ?? 0} interactions
        </span>
        {daysIn !== null && (
          <span style={{
            fontSize: 10, borderRadius: 4, padding: '2px 7px',
            color: daysIn > 10 ? '#d97706' : T.textMuted,
            background: daysIn > 10 ? 'rgba(217,119,6,0.08)' : T.surface2,
            border: `1px solid ${daysIn > 10 ? 'rgba(217,119,6,0.25)' : T.border}`,
          }}>
            📅 {daysIn}d in stage
          </span>
        )}
      </div>

      {/* Planned Start Date — required before Bid Review */}
      {['new', 'qualified'].includes(lead.stage) && (
        <div style={{ paddingLeft: 6, marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 10, color: lead.planned_start_date ? T.textMuted : '#d97706', fontWeight: 600 }}>
              Planned Start{!lead.planned_start_date && ' *'}
            </label>
            <input type="date"
              value={lead.planned_start_date ? lead.planned_start_date.slice(0, 10) : ''}
              onChange={async e => {
                try {
                  await api.crmUpdateLead(lead.id, { planned_start_date: e.target.value || null });
                  onRefresh();
                } catch (err) { alert(err.message || 'Failed to update'); }
              }}
              onClick={e => e.stopPropagation()}
              style={{
                flex: 1, padding: '3px 6px', fontSize: 10,
                background: T.inputBg, border: `1px solid ${lead.planned_start_date ? T.border : 'rgba(217,119,6,0.4)'}`,
                borderRadius: 4, color: T.text, fontFamily: "'DM Mono', monospace",
              }}
            />
          </div>
          {!lead.planned_start_date && (
            <span style={{ fontSize: 9, color: '#d97706', fontStyle: 'italic' }}>Required before Bid Review</span>
          )}
        </div>
      )}


      {/* Inline site address (+ W3W for construction) — construction and
          surveying leads at Qualified / Bidding stages. SUR Phase 1. */}
      {['qualified', 'bidding'].includes(lead.stage) && isAddressRequired({ project_type: lead.project_type }) && (
        <SiteAddressFields
          lead={lead}
          onRefresh={onRefresh}
          showW3W={lead.project_type === 'construction'}
        />
      )}

      {/* Advance button (New → Qualified → Bid Review) */}
      {canAdvance && (
        <button onClick={e => { e.stopPropagation(); handleAdvance(); }}
          style={{
            width: '100%', padding: '5px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600,
            cursor: 'pointer', border: `1px solid ${stage.color}33`,
            background: stage.color + '0c', color: stage.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 6,
          }}>
          Advance to {lead.stage === 'new' ? 'Qualified' : 'Bid Review'} <ChevronRight size={13} />
        </button>
      )}

      {/* Expand toggle for notes & interactions */}
      <button onClick={e => { e.stopPropagation(); handleExpand(); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 8px', background: T.surface2, border: `1px solid ${T.border}`,
          borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: T.textMuted,
        }}>
        <span>📋 BD Notes & Interactions ({lead.interaction_count ?? 0})</span>
        <span style={{ marginLeft: 'auto' }}>{expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ marginTop: 6, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: '0 0 6px 6px', padding: 10, fontSize: 11 }}
          onClick={e => e.stopPropagation()}>
          {/* BD Assessment */}
          {(lead.bd_assessment || lead.opening_notes) && <>
            <p style={{ color: T.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 5px' }}>BD Assessment</p>
            <p style={{ color: T.textMuted, lineHeight: 1.5, margin: '0 0 10px' }}>{lead.bd_assessment || lead.opening_notes}</p>
          </>}

          {/* Interaction history — collapsed: 3 rows, expanded: all + scrollable */}
          {interactions.length > 0 && <>
            <p style={{ color: T.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 5px' }}>Recent Interactions</p>
            <div style={{
              maxHeight: interactionExpanded ? 220 : 88,
              overflowY: interactionExpanded ? 'auto' : 'hidden',
              transition: 'max-height 0.3s ease',
            }}>
              {(interactionExpanded ? interactions : interactions.slice(0, 3)).map((a, i, arr) => {
                const s = INT_STYLES[a.interaction_type] || INT_STYLES.other;
                return (
                  <div key={a.id || i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', padding: '4px 0', borderBottom: i < arr.length - 1 ? `1px solid ${T.borderLight}` : 'none' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: s.bg, color: s.color, flexShrink: 0, marginTop: 1 }}>{s.label}</span>
                    <span style={{ color: T.textMuted, flex: 1, lineHeight: 1.4 }}>{a.summary || '—'}</span>
                    <span style={{ color: T.textDim, fontFamily: "'DM Mono', monospace", fontSize: 10, flexShrink: 0 }}>{shortDate(a.interaction_date)}</span>
                  </div>
                );
              })}
            </div>
          </>}

          {/* Log new interaction — triggers modal */}
          <div style={{ marginTop: 10, borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
            <button
              onClick={e => { e.stopPropagation(); setInteractionExpanded(true); }}
              style={{
                width: '100%', padding: '5px 8px', borderRadius: 5, fontSize: 11, fontWeight: 500,
                border: `1px dashed ${T.border}`, background: 'transparent', color: T.textDim,
                cursor: 'pointer', textAlign: 'left', transition: 'all 0.18s',
              }}>
              ＋ Add interaction…
            </button>
          </div>
          {/* Interaction modal */}
          {interactionExpanded && (
            <InteractionModal
              lead={lead}
              interactions={interactions}
              noteText={noteText}
              setNoteText={setNoteText}
              logging={logging}
              onLog={handleLog}
              onClose={() => { setInteractionExpanded(false); setNoteText(''); }}
              T={T}
            />
          )}
        </div>
      )}

      {/* Bid Review actions */}
      {isReview && canDecide && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button onClick={e => { e.stopPropagation(); handleDecision('bid'); }} disabled={deciding}
            style={{ flex: 1, padding: '6px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: 'rgba(5,150,105,0.12)', color: '#059669', border: '1px solid rgba(5,150,105,0.25)' }}>
            OK Approve — Bid
          </button>
          <button onClick={e => { e.stopPropagation(); handleDecision('no_bid'); }} disabled={deciding}
            style={{ flex: 1, padding: '6px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
            ✕ No Bid
          </button>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 9, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{
            width: 18, height: 18, borderRadius: '50%', background: T.tealDim, border: `1px solid ${T.tealGlow}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: T.teal,
          }}>
            {initials(lead.assigned_bd?.name || lead.assigned_manager?.name)}
          </div>
          <span style={{ color: T.textMuted, fontSize: 11 }}>
            {(lead.assigned_bd?.name || lead.assigned_manager?.name || '—').split(' ')[0]}
          </span>
        </div>
        <span style={{ color: isReview ? '#d97706' : T.textDim, fontSize: 10, fontFamily: "'DM Mono', monospace" }}>
          {isReview ? `⚠ ${daysIn}d waiting` : stage.id === 'won' ? `OK Won` : shortDate(lead.created_at)}
        </span>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// PIPELINE COLUMN
// ═══════════════════════════════════════════════════════════════════════════════

function PipelineColumn({ stage, leads, user, onRefresh, onHover, onLeave, onCardClick, onCRClick, onTenderCreated }) {
  const [expandedCards, setExpandedCards] = useState({});
  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  const [dragOver, setDragOver] = useState(false);
  const totalPence = leads.reduce((s, l) => s + Number(l.estimated_value_pence || 0), 0);
  let colVal = fmtVal(totalPence * 100);
  if (stage.id === 'bid_review') colVal += ' — awaiting decision';
  if (stage.id === 'bidding') colVal += ' in tender';
  if (stage.id === 'won') colVal += ' won this period';

  // DnD stages that accept drops (bidding/won/lost/no_bid are managed by decisions)
  const droppable = ['new', 'qualified', 'bid_review'].includes(stage.id);

  async function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (!droppable) return;
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.fromStage === stage.id) return;
      await api.crmMoveLead(data.leadId, stage.id);
      onRefresh();
    } catch (err) {
      alert(err.message || 'Cannot move lead to this stage');
    }
  }

  return (
    <div
      onDragOver={e => { if (droppable) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true); } }}
      onDragEnter={e => { if (droppable) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={handleDrop}
      style={{
        background: dragOver ? `${stage.color}12` : T.colBg,
        border: `1px solid ${dragOver ? stage.color : T.border}`,
        borderRadius: 10, overflow: 'visible', display: 'flex', flexDirection: 'column', minWidth: 0,
        transition: 'all 0.15s',
      }}
    >
      <div style={{ padding: '10px 13px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: stage.color }}>{stage.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '1px 8px', fontFamily: "'DM Mono', monospace" }}>{leads.length}</span>
      </div>
      <div style={{ padding: '4px 13px 9px', borderBottom: `2px solid ${stage.color}`, fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>
        {leads.length > 0 ? colVal : '—'}
      </div>
      <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
        {leads.length === 0 && <div style={{ padding: '20px 0', textAlign: 'center', color: T.textDim, fontSize: 12 }}>No leads</div>}
        {leads.map(lead => (
          <LeadCard key={lead.id} lead={lead} stage={stage} user={user} onRefresh={onRefresh} onHover={onHover} onLeave={onLeave} onCardClick={onCardClick} onCRClick={onCRClick} onTenderCreated={onTenderCreated} isExpanded={!!expandedCards[lead.id]} onToggleExpand={() => toggleCard(lead.id)} />
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// NEW LEAD MODAL (updated for B2 — includes opening_notes, classification)
// ═══════════════════════════════════════════════════════════════════════════════

function NewLeadModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', company_id: null, company_name: '', contact_id: null, contact_name: '', contact_role: 'client',
    source: 'inbound', estimated_value: '',
    opening_notes: '', classification_id: '', project_type: '',
    planned_start_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [classifications, setClassifications] = useState([]);

  // Company typeahead state
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [companyFocused, setCompanyFocused] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const companyRef = useRef(null);
  const debounceRef = useRef(null);

  // Contact state
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactFocused, setContactFocused] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone_country_code: '+44', phone: '', job_title: '' });
  const contactRef = useRef(null);
  const contactDebounceRef = useRef(null);

  useEffect(() => {
    api.crmClassifications?.().then(setClassifications).catch(() => {});
  }, []);

  // Debounced company search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!companySearch.trim() || companySearch.length < 2) { setCompanyResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const results = await api.crmCompanies(companySearch.trim());
        setCompanyResults(results);
      } catch { setCompanyResults([]); }
      setCompanyLoading(false);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [companySearch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) { if (companyRef.current && !companyRef.current.contains(e.target)) setCompanyFocused(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectCompany(company) {
    setForm(f => ({ ...f, company_id: company.id, company_name: company.name }));
    setCompanySearch(company.name);
    setCompanyFocused(false);
  }

  async function createAndSelectCompany(name) {
    try {
      const company = await api.crmCreateCompany({ name: name.trim() });
      selectCompany(company);
    } catch (e) { setError(e.message || 'Failed to create company'); }
  }

  // Debounced contact search
  useEffect(() => {
    if (contactDebounceRef.current) clearTimeout(contactDebounceRef.current);
    if (!contactSearch.trim() || contactSearch.length < 2) { setContactResults([]); return; }
    contactDebounceRef.current = setTimeout(async () => {
      setContactLoading(true);
      try {
        const params = { search: contactSearch.trim() };
        if (form.company_id) params.company_id = form.company_id;
        const results = await api.crmContacts(params);
        setContactResults(results);
      } catch { setContactResults([]); }
      setContactLoading(false);
    }, 300);
    return () => { if (contactDebounceRef.current) clearTimeout(contactDebounceRef.current); };
  }, [contactSearch, form.company_id]);

  // Close contact dropdown on outside click
  useEffect(() => {
    function handleClick(e) { if (contactRef.current && !contactRef.current.contains(e.target)) setContactFocused(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectContact(contact) {
    setForm(f => ({ ...f, contact_id: contact.id, contact_name: (contact.first_name + ' ' + contact.last_name).trim() || contact.name }));
    setContactSearch((contact.first_name + ' ' + contact.last_name).trim() || contact.name || '');
    setContactFocused(false);
    setShowNewContact(false);
  }

  async function createAndSelectContact() {
    if (!newContact.first_name.trim() || !newContact.last_name.trim()) {
      setError('Contact first and last name are required');
      return;
    }
    try {
      const contact = await api.crmCreateContact({
        first_name: newContact.first_name.trim(),
        last_name: newContact.last_name.trim(),
        email: newContact.email || null,
        phone: newContact.phone || null,
        phone_country_code: newContact.phone_country_code || '+44',
        job_title: newContact.job_title || null,
        company_id: form.company_id || null,
      });
      selectContact(contact);
      setNewContact({ first_name: '', last_name: '', email: '', phone_country_code: '+44', phone: '', job_title: '' });
    } catch (e) { setError(e.message || 'Failed to create contact'); }
  }

  async function handleSave() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.opening_notes.trim()) { setError('Opening notes are required'); return; }
    setSaving(true); setError('');
    try {
      // If user typed a company name but didn't select from dropdown, try to match/create
      let company_id = form.company_id;
      if (!company_id && companySearch.trim()) {
        const companies = await api.crmCompanies(companySearch.trim());
        const exactMatch = companies.find(c => c.name.toLowerCase() === companySearch.trim().toLowerCase());
        if (exactMatch) {
          company_id = exactMatch.id;
        } else {
          const newCo = await api.crmCreateCompany({ name: companySearch.trim() });
          company_id = newCo.id;
        }
      }
      await api.crmCreateLead({
        title: form.title.trim(),
        company_id,
        contact_id: form.contact_id || null,
        contact_role: form.contact_role || 'client',
        source: form.source,
        estimated_value_pence: form.estimated_value ? Math.round(parseFloat(form.estimated_value) * 100) : null,
        opening_notes: form.opening_notes.trim(),
        classification_id: form.classification_id || null,
        project_type: form.project_type || null,
        planned_start_date: form.planned_start_date || null,
      });
      onSaved(); onClose();
    } catch (e) { setError(e.message || 'Failed to create lead'); }
    setSaving(false);
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '8px 12px', background: T.inputBg,
    border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 13, boxSizing: 'border-box',
  };

  const showDropdown = companyFocused && companySearch.length >= 2;
  const exactMatch = companyResults.some(c => c.name.toLowerCase() === companySearch.trim().toLowerCase());

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--modal-bg)', borderRadius: 14, padding: 28, width: 440, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ color: T.text, margin: '0 0 20px', fontSize: 16 }}>New BD Lead</h3>

        {/* Lead Title */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Lead Title *</label>
          <input type="text" placeholder="Company — Project description" value={form.title} onChange={e => set('title', e.target.value)} style={inputStyle} />
        </div>

        {/* Company Typeahead */}
        <div style={{ marginBottom: 12, position: 'relative' }} ref={companyRef}>
          <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>
            Company *
            {form.company_id && <span style={{ color: T.teal, marginLeft: 6, fontSize: 10 }}>OK {form.company_name}</span>}
          </label>
          <input type="text" placeholder="Search existing or type new…"
            value={companySearch}
            onChange={e => {
              setCompanySearch(e.target.value);
              setForm(f => ({ ...f, company_id: null, company_name: '' }));
            }}
            onFocus={() => setCompanyFocused(true)}
            style={{
              ...inputStyle,
              borderColor: form.company_id ? T.teal : T.border,
            }}
          />
          {showDropdown && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
              background: 'var(--modal-bg)', border: `1px solid ${T.border}`, borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto', marginTop: 2,
            }}>
              {companyLoading && (
                <div style={{ padding: '8px 12px', color: T.textDim, fontSize: 12 }}>Searching…</div>
              )}
              {!companyLoading && companyResults.length === 0 && companySearch.length >= 2 && (
                <div style={{ padding: '8px 12px', color: T.textDim, fontSize: 12 }}>No matching companies</div>
              )}
              {companyResults.map(c => (
                <div key={c.id}
                  onClick={() => selectCompany(c)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: T.text,
                    borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: 11, color: T.teal }}>🏢</span>
                  <span>{c.name}</span>
                  {c.leads_count > 0 && (
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: T.textDim }}>{c.leads_count} lead{c.leads_count !== 1 ? 's' : ''}</span>
                  )}
                </div>
              ))}
              {!companyLoading && !exactMatch && companySearch.trim().length >= 2 && (
                <div
                  onClick={() => createAndSelectCompany(companySearch)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: T.teal, fontWeight: 600,
                    borderTop: companyResults.length > 0 ? `1px solid ${T.border}` : 'none',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = T.tealDim}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Plus size={14} /> Add "{companySearch.trim()}" as new company
                </div>
              )}
            </div>
          )}
        </div>

        {/* Estimated Value */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Estimated Value (£)</label>
          <input type="number" placeholder="0.00" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} style={inputStyle} />
        </div>

        {/* ── Contact Section ── */}
        <div style={{ marginBottom: 12, padding: '12px', background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginBottom: 8 }}>Primary Contact</div>

          {/* Contact Typeahead */}
          <div style={{ position: 'relative', marginBottom: 8 }} ref={contactRef}>
            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>
              Search Contact
              {form.contact_id && <span style={{ color: T.teal, marginLeft: 6 }}>OK {form.contact_name}</span>}
              {!form.company_id && <span style={{ color: T.textDim, marginLeft: 6, fontStyle: 'italic' }}>(select company first for filtered results)</span>}
            </label>
            <input type="text" placeholder="Search by name or email…"
              value={contactSearch}
              onChange={e => { setContactSearch(e.target.value); setForm(f => ({ ...f, contact_id: null, contact_name: '' })); setShowNewContact(false); }}
              onFocus={() => setContactFocused(true)}
              style={{ ...inputStyle, fontSize: 12, padding: '6px 10px', borderColor: form.contact_id ? T.teal : T.border }}
            />
            {contactFocused && contactSearch.length >= 2 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                background: 'var(--modal-bg)', border: `1px solid ${T.border}`, borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 180, overflowY: 'auto', marginTop: 2,
              }}>
                {contactLoading && <div style={{ padding: '6px 10px', color: T.textDim, fontSize: 11 }}>Searching…</div>}
                {!contactLoading && contactResults.length === 0 && <div style={{ padding: '6px 10px', color: T.textDim, fontSize: 11 }}>No matching contacts</div>}
                {contactResults.map(c => (
                  <div key={c.id} onClick={() => selectContact(c)}
                    style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, color: T.text, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ color: T.teal, fontSize: 10 }}>*</span>
                    <span>{c.first_name} {c.last_name}</span>
                    {c.job_title && <span style={{ color: T.textDim, fontSize: 10 }}>· {c.job_title}</span>}
                    {c.company_name && <span style={{ marginLeft: 'auto', color: T.textDim, fontSize: 10 }}>{c.company_name}</span>}
                  </div>
                ))}
                <div onClick={() => { setShowNewContact(true); setContactFocused(false); }}
                  style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, color: T.teal, fontWeight: 600, borderTop: contactResults.length > 0 ? `1px solid ${T.border}` : 'none', display: 'flex', alignItems: 'center', gap: 5 }}
                  onMouseEnter={e => e.currentTarget.style.background = T.tealDim}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Plus size={13} /> Add new contact
                </div>
              </div>
            )}
          </div>

          {/* Inline New Contact Form */}
          {showNewContact && (
            <div style={{ padding: 10, background: 'var(--modal-bg)', border: `1px solid ${T.teal}33`, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.teal, marginBottom: 8 }}>New Contact</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={{ color: T.textDim, fontSize: 9, display: 'block', marginBottom: 2 }}>First Name *</label>
                  <input type="text" placeholder="First name" value={newContact.first_name}
                    onChange={e => setNewContact(c => ({ ...c, first_name: e.target.value }))}
                    style={{ ...inputStyle, fontSize: 11, padding: '5px 8px' }} />
                </div>
                <div>
                  <label style={{ color: T.textDim, fontSize: 9, display: 'block', marginBottom: 2 }}>Last Name *</label>
                  <input type="text" placeholder="Last name" value={newContact.last_name}
                    onChange={e => setNewContact(c => ({ ...c, last_name: e.target.value }))}
                    style={{ ...inputStyle, fontSize: 11, padding: '5px 8px' }} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: T.textDim, fontSize: 9, display: 'block', marginBottom: 2 }}>Email</label>
                <input type="email" placeholder="email@company.com" value={newContact.email}
                  onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))}
                  style={{ ...inputStyle, fontSize: 11, padding: '5px 8px' }} />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: T.textDim, fontSize: 9, display: 'block', marginBottom: 2 }}>Phone</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select value={newContact.phone_country_code}
                    onChange={e => setNewContact(c => ({ ...c, phone_country_code: e.target.value }))}
                    style={{ ...inputStyle, fontSize: 11, padding: '5px 6px', width: 90, flexShrink: 0 }}>
                    <option value="+44">UK +44</option>
                    <option value="+1">US +1</option>
                    <option value="+353">IE +353</option>
                    <option value="+33">FR +33</option>
                    <option value="+49">DE +49</option>
                    <option value="+31">NL +31</option>
                    <option value="+34">ES +34</option>
                    <option value="+39">IT +39</option>
                    <option value="+46">SE +46</option>
                    <option value="+47">NO +47</option>
                    <option value="+45">DK +45</option>
                    <option value="+61">AU +61</option>
                    <option value="+971">AE +971</option>
                    <option value="+966">SA +966</option>
                    <option value="+91">IN +91</option>
                  </select>
                  <input type="tel" placeholder="07777 888333" value={newContact.phone}
                    onChange={e => setNewContact(c => ({ ...c, phone: e.target.value }))}
                    style={{ ...inputStyle, fontSize: 11, padding: '5px 8px', flex: 1 }} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: T.textDim, fontSize: 9, display: 'block', marginBottom: 2 }}>Job Title</label>
                <input type="text" placeholder="e.g. Project Manager" value={newContact.job_title}
                  onChange={e => setNewContact(c => ({ ...c, job_title: e.target.value }))}
                  style={{ ...inputStyle, fontSize: 11, padding: '5px 8px' }} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={createAndSelectContact}
                  style={{ padding: '5px 12px', background: T.teal, color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                  Save Contact
                </button>
                <button onClick={() => setShowNewContact(false)}
                  style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5, fontSize: 11, color: T.textMuted, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Contact Role */}
          <div>
            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Contact Role on This Lead</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['client', 'Client'], ['consultant', 'Consultant']].map(([val, label]) => (
                <button key={val} onClick={() => set('contact_role', val)}
                  style={{
                    flex: 1, padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', border: `1px solid ${form.contact_role === val ? T.teal : T.border}`,
                    background: form.contact_role === val ? T.tealDim : 'transparent',
                    color: form.contact_role === val ? T.teal : T.textMuted,
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Source *</label>
          <select value={form.source} onChange={e => set('source', e.target.value)} style={inputStyle}>
            {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Opening Notes * <span style={{ fontSize: 10, fontWeight: 400 }}>(mandatory)</span></label>
          <textarea value={form.opening_notes} onChange={e => set('opening_notes', e.target.value)} rows={3}
            placeholder="Initial assessment — what's the opportunity, why should we bid?"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Classification</label>
            <select value={form.classification_id} onChange={e => set('classification_id', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              {classifications.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Project Type</label>
            <select value={form.project_type} onChange={e => set('project_type', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              <option value="construction">Construction</option>
              <option value="consultancy">Consultancy</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Planned Start Date</label>
          <input type="date" value={form.planned_start_date} onChange={e => set('planned_start_date', e.target.value)} style={inputStyle} />
        </div>

        {error && <p style={{ color: '#ef4444', fontSize: 12, margin: '0 0 12px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 18px', background: T.teal, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Creating…' : 'Create Lead'}
          </button>
        </div>
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════════
// EDIT LEAD MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function EditLeadModal({ lead, onClose, onSaved }) {
  const { user } = useUser();
  const [form, setForm] = useState({
    title: lead.title || '',
    estimated_value: lead.estimated_value || '',
    source: lead.source || 'cold_outreach',
    opening_notes: lead.opening_notes || '',
    bd_assessment: lead.bd_assessment || '',
    classification_id: lead.classification?.id || '',
    project_type: lead.project_type || '',
    planned_start_date: lead.planned_start_date ? lead.planned_start_date.slice(0, 10) : '',
    contact_id: lead.contact?.id || null,
    contact_name: lead.contact?.name || '',
    contact_role: lead.contact_role || 'client',
    company_id: lead.company?.id || null,
    company_name: lead.company?.name || '',
    assigned_bd_id: lead.assigned_bd?.id || '',
    site_address: lead.site_address || '',
    what3words: lead.what3words || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [classifications, setClassifications] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [bdUsers, setBdUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [archiving, setArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  // Contact search state
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [contactFocused, setContactFocused] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '', phone_country_code: '+44', phone: '', job_title: '' });
  const contactRef = useRef(null);
  const contactDebounceRef = useRef(null);

  // Company search state (manager/director only)
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [companyFocused, setCompanyFocused] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const companyRef = useRef(null);
  const companyDebounceRef = useRef(null);

  const canEdit = ['bd', 'manager', 'director'].includes(user?.role);
  const canEditCompany = ['manager', 'director'].includes(user?.role);
  const canArchive = ['manager', 'director'].includes(user?.role);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.crmClassifications().then(setClassifications).catch(() => {});
    api.getUsers().then(users => setBdUsers(users.filter(u => ['bd','manager','director'].includes(u.role) && u.is_active))).catch(() => {});
    api.crmLeadAudit(lead.id).then(setAuditLog).catch(() => {});
  }, [lead.id]);

  // Contact typeahead
  useEffect(() => {
    if (contactSearch.length < 2) { setContactResults([]); return; }
    if (contactDebounceRef.current) clearTimeout(contactDebounceRef.current);
    setContactLoading(true);
    contactDebounceRef.current = setTimeout(async () => {
      try {
        const params = { search: contactSearch };
        if (form.company_id) params.company_id = form.company_id;
        const results = await api.crmContacts(params);
        setContactResults(results);
      } catch (e) {}
      setContactLoading(false);
    }, 300);
    return () => { if (contactDebounceRef.current) clearTimeout(contactDebounceRef.current); };
  }, [contactSearch, form.company_id]);

  // Company typeahead (manager only)
  useEffect(() => {
    if (!canEditCompany || companySearch.length < 2) { setCompanyResults([]); return; }
    if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
    setCompanyLoading(true);
    companyDebounceRef.current = setTimeout(async () => {
      try {
        const results = await api.crmCompanies(companySearch);
        setCompanyResults(results);
      } catch (e) {}
      setCompanyLoading(false);
    }, 300);
    return () => { if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current); };
  }, [companySearch, canEditCompany]);

  // Click outside handlers
  useEffect(() => {
    function handleClick(e) {
      if (contactRef.current && !contactRef.current.contains(e.target)) setContactFocused(false);
      if (companyRef.current && !companyRef.current.contains(e.target)) setCompanyFocused(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectContact(c) {
    set('contact_id', c.id);
    set('contact_name', `${c.first_name} ${c.last_name}`);
    setContactSearch('');
    setContactFocused(false);
  }

  async function createAndSelectContact() {
    if (!newContact.first_name.trim() || !newContact.last_name.trim()) return;
    try {
      const c = await api.crmCreateContact({ ...newContact, company_id: form.company_id });
      selectContact(c);
      setShowNewContact(false);
      setNewContact({ first_name: '', last_name: '', email: '', phone_country_code: '+44', phone: '', job_title: '' });
    } catch (e) { setError(e.message); }
  }

  async function handleSave() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        title: form.title,
        estimated_value_pence: form.estimated_value ? Math.round(parseFloat(form.estimated_value) * 100) : null,
        source: form.source,
        opening_notes: form.opening_notes,
        bd_assessment: form.bd_assessment,
        classification_id: form.classification_id || null,
        project_type: form.project_type || null,
        planned_start_date: form.planned_start_date || null,
        contact_id: form.contact_id,
        contact_role: form.contact_role,
      site_address: form.site_address || null,
      what3words: form.what3words || null,
      };
      if (canEditCompany) payload.company_id = form.company_id;
      if (canEditCompany) payload.assigned_bd_id = form.assigned_bd_id || null;
      payload.site_address = form.site_address || null;
      payload.what3words = form.what3words || null;
      await api.crmUpdateLead(lead.id, payload);
      onSaved();
      onClose();
    } catch (e) { setError(e.message || 'Save failed'); }
    setSaving(false);
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await api.crmArchiveLead(lead.id);
      onSaved();
      onClose();
    } catch (e) { setError(e.message || 'Archive failed'); }
    setArchiving(false);
  }

  async function handleRestore() {
    try {
      await api.crmRestoreLead(lead.id);
      onSaved();
      onClose();
    } catch (e) { setError(e.message || 'Restore failed'); }
  }

  const inputStyle = { width: '100%', padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' };

  const FIELD_LABELS = {
    title: 'Title', estimated_value_pence: 'Est. Value', source: 'Source', opening_notes: 'Opening Notes',
    bd_assessment: 'BD Assessment', classification_id: 'Classification', project_type: 'Project Type',
    planned_start_date: 'Planned Start', contact_id: 'Contact', contact_role: 'Contact Role',
    company_id: 'Company', stage: 'Stage', is_archived: 'Archived',
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--modal-bg)', border: `1px solid ${T.border}`, borderRadius: 14, width: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{lead.title}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>{lead.enq_ref ? `${lead.tender_ref || lead.enq_ref} \u2014 ` : ''}{lead.company?.name}</div>
          </div>
          {lead.is_archived && <span style={{ padding: '3px 10px', background: '#7f1d1d', color: '#fca5a5', borderRadius: 5, fontSize: 10, fontWeight: 600 }}>ARCHIVED</span>}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${T.border}`, padding: '0 20px' }}>
          {['details', 'audit'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ padding: '8px 16px', fontSize: 12, fontWeight: activeTab === tab ? 600 : 400, color: activeTab === tab ? T.teal : T.textMuted, background: 'transparent', border: 'none', borderBottom: activeTab === tab ? `2px solid ${T.teal}` : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize' }}>
              {tab === 'details' ? 'Edit Details' : `Audit Log (${auditLog.length})`}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {activeTab === 'details' && (
            <>
              {/* Title */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Title *</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} style={inputStyle} disabled={!canEdit} />
              </div>

              {/* Company (read-only for BD, editable for manager/director) */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Company {!canEditCompany && <span style={{ fontSize: 9, fontStyle: 'italic' }}>(manager/director to change)</span>}</label>
                {canEditCompany ? (
                  <div ref={companyRef} style={{ position: 'relative' }}>
                    <input type="text" placeholder="Search company..." value={companySearch || form.company_name}
                      onChange={e => { setCompanySearch(e.target.value); set('company_id', null); set('company_name', ''); }}
                      onFocus={() => setCompanyFocused(true)}
                      style={{ ...inputStyle, borderColor: form.company_id ? T.teal : T.border }} />
                    {companyFocused && companySearch.length >= 2 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'var(--modal-bg)', border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 150, overflowY: 'auto', marginTop: 2 }}>
                        {companyResults.map(c => (
                          <div key={c.id} onClick={() => { set('company_id', c.id); set('company_name', c.name); setCompanySearch(''); setCompanyFocused(false); }}
                            style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, color: T.text, borderBottom: `1px solid ${T.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {c.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ ...inputStyle, background: T.surface2, color: T.textMuted }}>{form.company_name}</div>
                )}
              </div>

              {/* Assigned BD (manager/director only) */}
              {canEditCompany && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Assigned BD</label>
                  <select value={form.assigned_bd_id} onChange={e => set('assigned_bd_id', e.target.value)} style={inputStyle}>
                    <option value="">-- Unassigned --</option>
                    {bdUsers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.initials})</option>)}
                  </select>
                </div>
              )}
              {!canEditCompany && lead.assigned_bd && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Assigned BD</label>
                  <div style={{ ...inputStyle, background: T.surface2, color: T.textMuted }}>{lead.assigned_bd.name}</div>
                </div>
              )}

              {/* Estimated Value + Source */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Estimated Value</label>
                  <input type="number" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} style={inputStyle} disabled={!canEdit} />
                </div>
                <div>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Source</label>
                  <select value={form.source} onChange={e => set('source', e.target.value)} style={inputStyle} disabled={!canEdit}>
                    {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>

              {/* Contact Section */}
              <div style={{ marginBottom: 12, padding: 12, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.text, marginBottom: 8 }}>Contact</div>
                <div style={{ position: 'relative', marginBottom: 8 }} ref={contactRef}>
                  <input type="text" placeholder="Search by name or email..."
                    value={contactSearch || (form.contact_id ? form.contact_name : '')}
                    onChange={e => { setContactSearch(e.target.value); set('contact_id', null); set('contact_name', ''); setShowNewContact(false); }}
                    onFocus={() => setContactFocused(true)}
                    style={{ ...inputStyle, fontSize: 12, borderColor: form.contact_id ? T.teal : T.border }}
                    disabled={!canEdit} />
                  {contactFocused && contactSearch.length >= 2 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: 'var(--modal-bg)', border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 160, overflowY: 'auto', marginTop: 2 }}>
                      {contactResults.map(c => (
                        <div key={c.id} onClick={() => selectContact(c)}
                          style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, color: T.text, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 6 }}
                          onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <span>{c.first_name} {c.last_name}</span>
                          {c.job_title && <span style={{ color: T.textDim, fontSize: 10 }}>{c.job_title}</span>}
                        </div>
                      ))}
                      <div onClick={() => { setShowNewContact(true); setContactFocused(false); }}
                        style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, color: T.teal, fontWeight: 600, borderTop: contactResults.length > 0 ? `1px solid ${T.border}` : 'none', display: 'flex', alignItems: 'center', gap: 5 }}
                        onMouseEnter={e => e.currentTarget.style.background = T.tealDim}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <Plus size={13} /> Add new contact
                      </div>
                    </div>
                  )}
                </div>

                {/* Inline new contact */}
                {showNewContact && (
                  <div style={{ padding: 10, background: 'var(--modal-bg)', border: `1px solid ${T.teal}33`, borderRadius: 8, marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <input type="text" placeholder="First name *" value={newContact.first_name} onChange={e => setNewContact(c => ({ ...c, first_name: e.target.value }))} style={{ ...inputStyle, fontSize: 11 }} />
                      <input type="text" placeholder="Last name *" value={newContact.last_name} onChange={e => setNewContact(c => ({ ...c, last_name: e.target.value }))} style={{ ...inputStyle, fontSize: 11 }} />
                    </div>
                    <input type="email" placeholder="Email" value={newContact.email} onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))} style={{ ...inputStyle, fontSize: 11, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      <select value={newContact.phone_country_code} onChange={e => setNewContact(c => ({ ...c, phone_country_code: e.target.value }))} style={{ ...inputStyle, fontSize: 11, width: 90 }}>
                        <option value="+44">UK +44</option><option value="+1">US +1</option><option value="+353">IE +353</option>
                        <option value="+33">FR +33</option><option value="+49">DE +49</option><option value="+31">NL +31</option>
                      </select>
                      <input type="tel" placeholder="Phone" value={newContact.phone} onChange={e => setNewContact(c => ({ ...c, phone: e.target.value }))} style={{ ...inputStyle, fontSize: 11, flex: 1 }} />
                    </div>
                    <input type="text" placeholder="Job title" value={newContact.job_title} onChange={e => setNewContact(c => ({ ...c, job_title: e.target.value }))} style={{ ...inputStyle, fontSize: 11, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={createAndSelectContact} style={{ padding: '5px 12px', background: T.teal, color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                      <button onClick={() => setShowNewContact(false)} style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5, fontSize: 11, color: T.textMuted, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Contact role */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {[['client', 'Client'], ['consultant', 'Consultant']].map(([val, label]) => (
                    <button key={val} onClick={() => canEdit && set('contact_role', val)}
                      style={{ flex: 1, padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: canEdit ? 'pointer' : 'default', border: `1px solid ${form.contact_role === val ? T.teal : T.border}`, background: form.contact_role === val ? T.tealDim : 'transparent', color: form.contact_role === val ? T.teal : T.textMuted }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Classification + Project Type */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Classification</label>
                  <select value={form.classification_id} onChange={e => set('classification_id', e.target.value)} style={inputStyle} disabled={!canEdit}>
                    <option value="">-- Select --</option>
                    {classifications.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Project Type</label>
                  <select value={form.project_type} onChange={e => set('project_type', e.target.value)} style={inputStyle} disabled={!canEdit}>
                    <option value="">-- Select --</option>
                    <option value="construction">Construction</option>
                    <option value="consultancy">Consultancy</option>
                  </select>
                </div>
              </div>

              {/* Planned Start */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Planned Start Date</label>
                <input type="date" value={form.planned_start_date} onChange={e => set('planned_start_date', e.target.value)} style={inputStyle} disabled={!canEdit} />
              </div>

              {/* Site Address (construction + surveying) + W3W (construction only). SUR Phase 1. */}
              {isAddressRequired(form) && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Site Address *</label>
                  <textarea value={form.site_address} onChange={e => set('site_address', e.target.value)} rows={2} placeholder="Full site address..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} disabled={!canEdit} />
                </div>
              )}
              {isW3WRequired(form) && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>What3Words *</label>
                  <input value={form.what3words} onChange={e => set('what3words', e.target.value.toLowerCase().replace(/[^a-z.]/g, ''))} placeholder="word.word.word" style={{ ...inputStyle, borderColor: form.what3words && !W3W_REGEX.test(form.what3words) ? '#ef4444' : form.what3words && W3W_REGEX.test(form.what3words) ? T.teal : T.border }} disabled={!canEdit} />
                  {form.what3words && !W3W_REGEX.test(form.what3words) && <div style={{ color: '#ef4444', fontSize: 10, marginTop: 3 }}>Format: word.word.word (lowercase)</div>}
                </div>
              )}

              {/* Opening Notes */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>Opening Notes</label>
                <textarea value={form.opening_notes} onChange={e => set('opening_notes', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} disabled={!canEdit} />
              </div>

              {/* BD Assessment */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ color: T.textMuted, fontSize: 11, display: 'block', marginBottom: 4 }}>BD Assessment</label>
                <textarea value={form.bd_assessment} onChange={e => set('bd_assessment', e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} disabled={!canEdit} />
              </div>
            </>
          )}

          {activeTab === 'audit' && (
            <div>
              {auditLog.length === 0 && <div style={{ color: T.textDim, fontSize: 12, textAlign: 'center', padding: 20 }}>No audit history yet</div>}
              {auditLog.map((entry, i) => (
                <div key={entry.id || i} style={{ padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 11 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontWeight: 600, color: T.text }}>{entry.user_name || 'System'}</span>
                    <span style={{ color: T.textDim, fontFamily: "'DM Mono', monospace", fontSize: 10 }}>{new Date(entry.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div style={{ color: T.textMuted }}>
                    <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600, marginRight: 6, background: entry.action === 'edit' ? T.tealDim : entry.action === 'archive' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)', color: entry.action === 'edit' ? T.teal : entry.action === 'archive' ? '#ef4444' : '#3b82f6' }}>
                      {entry.action.toUpperCase()}
                    </span>
                    {FIELD_LABELS[entry.field_name] || entry.field_name}:
                    {entry.old_value && <span style={{ textDecoration: 'line-through', marginLeft: 4, color: T.textDim }}>{entry.old_value.length > 20 && entry.old_value.includes('GMT') ? entry.old_value.slice(0, 10) : entry.old_value}</span>}
                    <span style={{ marginLeft: 4, color: T.text }}>{entry.new_value || '(cleared)'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          {canArchive && !lead.is_archived && !showArchiveConfirm && (
            lead.tender_ref ? (
              <span style={{ fontSize: 10, color: T.textDim, fontStyle: 'italic' }}>
                Manage from All Tenders
              </span>
            ) : (
              <button onClick={() => setShowArchiveConfirm(true)}
                style={{ padding: '7px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#dc2626', fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                Archive
              </button>
            )
          )}
          {showArchiveConfirm && (
            <>
              <span style={{ fontSize: 11, color: '#dc2626' }}>Archive this lead?</span>
              <button onClick={handleArchive} disabled={archiving}
                style={{ padding: '5px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {archiving ? 'Archiving...' : 'Confirm'}
              </button>
              <button onClick={() => setShowArchiveConfirm(false)}
                style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 5, fontSize: 11, color: T.textMuted, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          )}
          {canArchive && lead.is_archived && (
            <button onClick={handleRestore}
              style={{ padding: '7px 14px', background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.25)', borderRadius: 6, color: '#059669', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Restore Lead
            </button>
          )}
          {error && <span style={{ color: '#ef4444', fontSize: 11, flex: 1 }}>{error}</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '7px 16px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7, color: T.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
            {canEdit && (
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '7px 18px', background: T.teal, border: 'none', borderRadius: 7, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// COMPANY DIRECTORY
// ═══════════════════════════════════════════════════════════════════════════════

export default function CRMPipeline({ onTenderCreated }) {
  const { user } = useUser();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [showNewLead, setShowNewLead] = useState(false);
  const [viewMode, setViewMode] = useState('Pipeline');
  const [listSort, setListSort] = useState({ field: 'stage', dir: 'asc' });
  const [lostLeads, setLostLeads] = useState([]);
  const [lostLoading, setLostLoading] = useState(false);
  const [lostSelected, setLostSelected] = useState(null);
  useEffect(() => {
    if (viewMode !== 'Lost') return;
    setLostLoading(true);
    import('../api').then(({ api }) => {
      api.get('/api/crm/leads/lost')
        .then(r => setLostLeads(r.data || []))
        .catch(() => setLostLeads([]))
        .finally(() => setLostLoading(false));
    });
  }, [viewMode]);
  const [lostEscalations, setLostEscalations] = useState([]);
  const [crLead, setCrLead] = useState(null);

  useEffect(() => {
    if (!['manager','director'].includes(user?.role)) return;
    import('../api').then(({ api }) => {
      api.crmLostEscalations().then(data => setLostEscalations(data || [])).catch(() => {});
    });
  }, [user?.role]);
  const [showLogModal, setShowLogModal] = useState(false);

  // Set default view for Feeder role
  useEffect(() => {
    if (user?.role === 'admin_clerk') setViewMode('Inbox');
  }, [user?.role]);

  // Set default view for Feeder role
  useEffect(() => {
    if (user?.role === 'admin_clerk') setViewMode('Inbox');
  }, [user?.role]);
  const [editLead, setEditLead] = useState(null);
  const [tooltip, setTooltip] = useState({ lead: null, pos: null });

  const canCreate = ['bd', 'manager', 'director'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [l, s] = await Promise.all([api.crmLeads(), api.crmPipeline()]);
      setLeads(l); setSummary(s);
    } catch (e) { setError(e.message || 'Failed to load pipeline'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredLeads = leads.filter(l => {
    if (filterSource !== 'all' && l.source !== filterSource) return false;
    if (search) {
      const q = search.toLowerCase();
      return [l.title, l.company?.name, l.contact?.name, l.enq_ref].some(v => v?.toLowerCase().includes(q));
    }
    return true;
  });

  const byStage = {};
  STAGES.forEach(s => { byStage[s.id] = []; });
  filteredLeads.forEach(l => { if (byStage[l.stage] !== undefined) byStage[l.stage].push(l); });

  const kpis = summary?.summary || {};
  const awaitingDecision = leads.filter(l => l.stage === 'bid_review').length;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: T.textMuted, fontSize: 13 }}>Loading pipeline…</div>;

  return (
    <div>
      {/* Page header with KPIs */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ color: T.text, margin: 0, fontSize: 20, fontWeight: 600 }}>BD <span style={{ color: T.teal }}>Pipeline</span></h2>
        <div style={{ display: 'flex', gap: 20 }}>
          {[
            { val: kpis.pipeline_value ? `£${(Number(kpis.pipeline_value) / 1000).toFixed(0)}k` : '—', label: 'Pipeline Value', color: T.teal },
            { val: kpis.active_leads ?? '—', label: 'Active Leads', color: T.text },
            { val: awaitingDecision || 0, label: 'Awaiting Decision', color: '#d97706' },
          ].map(({ val, label, color }) => (
            <div key={label} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: "'DM Mono', monospace" }}>{val}</div>
              <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, padding: 3 }}>
          {(user?.role === 'admin_clerk' ? ['Inbox'] : ['Pipeline', 'List', 'Companies', 'Contacts', ...(['estimator','senior_viewer'].includes(user?.role) ? [] : ['Inbox'])]).map(v => (
            <button key={v} onClick={() => setViewMode(v)} style={{
              padding: '4px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer', border: 'none',
              background: v === viewMode ? T.teal : 'transparent',
              color: v === viewMode ? '#fff' : T.textMuted,
              fontWeight: v === viewMode ? 600 : 400, fontFamily: 'inherit',
            }}>{v}</button>
          ))}
        </div>
        {(user?.role === 'admin_clerk' ? [] : [['all', 'All'], ['referral', 'Referral'], ['framework', 'Framework'], ['repeat_client', 'Repeat'], ['cold_outreach', 'Cold Outreach']]).map(([val, label]) => (
          <button key={val} onClick={() => setFilterSource(val)}
            style={{
              padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${filterSource === val ? T.teal : T.border}`,
              background: filterSource === val ? T.tealDim : 'transparent',
              color: filterSource === val ? T.teal : T.textMuted,
            }}>{label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: T.textMuted }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads…"
              style={{ paddingLeft: 28, paddingRight: 12, paddingTop: 7, paddingBottom: 7, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, fontSize: 12, width: 200, fontFamily: 'inherit' }} />
          </div>
          <button onClick={load} style={{ padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><RefreshCw size={15} /></button>
          {user?.role === 'admin_clerk' && viewMode === 'Inbox' && (
            <button onClick={() => setShowLogModal(true)}
              style={{ padding: '7px 14px', background: T.teal, border: 'none', borderRadius: 7, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, fontFamily: 'inherit' }}>
              📝 Log Inbound
            </button>
          )}
          {canCreate && (
            <button onClick={() => setShowNewLead(true)}
              style={{ padding: '7px 14px', background: T.teal, border: 'none', borderRadius: 7, color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, fontFamily: 'inherit' }}>
              <Plus size={15} /> New Lead
            </button>
          )}
        </div>
      </div>

      {viewMode === 'List' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'inherit' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${T.border}`, background: T.surface2 }}>
                {[
                  ['enq_ref', 'ENQ Ref'],
                  ['title', 'Project Name'],
                  ['company', 'Company'],
                  ['stage', 'Stage'],
                  ['source', 'Source'],
                  ['estimated_value_pence', 'Est. Value'],
                  ['assigned_bd', 'BD'],
                  ['planned_start_date', 'Planned Start'],
                  ['created_at', 'Logged'],
                ].map(([key, label]) => (
                  <th key={key} onClick={() => {
                    if (listSort.field === key) setListSort(s => ({ field: key, dir: s.dir === 'asc' ? 'desc' : 'asc' }));
                    else setListSort({ field: key, dir: 'asc' });
                  }} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.05em', fontSize: 10, color: T.textMuted, cursor: 'pointer', whiteSpace: 'nowrap',
                    userSelect: 'none' }}>
                    {label} {listSort.field === key ? (listSort.dir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...filteredLeads].sort((a, b) => {
                const { field, dir } = listSort;
                let av, bv;
                if (field === 'company') { av = a.company?.name || ''; bv = b.company?.name || ''; }
                else if (field === 'assigned_bd') { av = a.assigned_bd?.name || ''; bv = b.assigned_bd?.name || ''; }
                else { av = a[field] || ''; bv = b[field] || ''; }
                if (typeof av === 'number') return dir === 'asc' ? av - bv : bv - av;
                return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
              }).map((lead, i) => {
                const stageConfig = STAGES.find(s => s.id === lead.stage);
                return (
                  <tr key={lead.id} onClick={() => setEditLead(lead)}
                    style={{ borderBottom: `1px solid ${T.border}`, cursor: 'pointer',
                      background: i % 2 === 0 ? T.surface : T.surface2 }}
                    onMouseEnter={e => e.currentTarget.style.background = T.tealDim}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.surface : T.surface2}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 11, color: T.teal, whiteSpace: 'nowrap' }}>
                      {lead.enq_ref || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 500, color: T.text, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lead.title}
                    </td>
                    <td style={{ padding: '10px 12px', color: T.textMuted, whiteSpace: 'nowrap' }}>
                      {lead.company?.name || '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: stageConfig ? `${stageConfig.color}18` : T.surface2,
                        color: stageConfig?.color || T.textMuted, border: `1px solid ${stageConfig ? stageConfig.color + '40' : T.border}`,
                        whiteSpace: 'nowrap' }}>
                        {stageConfig?.label || lead.stage}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: T.textMuted, whiteSpace: 'nowrap' }}>
                      {lead.source ? lead.source.replace(/_/g, ' ') : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: T.text, whiteSpace: 'nowrap' }}>
                      {lead.estimated_value_pence ? '£' + (lead.estimated_value_pence / 100).toLocaleString('en-GB', { maximumFractionDigits: 0 }) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: T.textMuted, whiteSpace: 'nowrap' }}>
                      {lead.assigned_bd?.name || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: T.textMuted, whiteSpace: 'nowrap' }}>
                      {lead.planned_start_date ? new Date(lead.planned_start_date).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: T.textDim, whiteSpace: 'nowrap', fontSize: 11 }}>
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                );
              })}
              {filteredLeads.length === 0 && (
                <tr><td colSpan={9} style={{ padding: '32px', textAlign: 'center', color: T.textDim }}>No leads found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'Inbox' && <InboxView user={user} onPromoted={load} onLog={() => setShowLogModal(true)} />}
      {showLogModal && (
        <CRMInboundForm
          isOpen={showLogModal}
          onClose={() => setShowLogModal(false)}
          onCreated={() => { setShowLogModal(false); load(); }}
        />
      )}

      {viewMode === 'Lost' && (
        <div style={{ padding: '0 24px 32px' }}>
          {lostLoading && <div style={{ textAlign: 'center', padding: 40, color: T.textMuted, fontSize: 13 }}>Loading…</div>}
          {!lostLoading && lostLeads.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: T.textDim, fontSize: 13 }}>No lost or dead leads found.</div>
          )}
          {!lostLoading && lostLeads.length > 0 && (
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Left: list */}
              <div style={{ flex: '0 0 360px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lostLeads.map(lead => (
                  <div key={lead.id} onClick={() => setLostSelected(lostSelected?.id === lead.id ? null : lead)}
                    style={{
                      background: lostSelected?.id === lead.id ? T.tealDim : T.surface,
                      border: `1px solid ${lostSelected?.id === lead.id ? T.teal : T.border}`,
                      borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{lead.title}</span>
                      {lead.tender_ref && (
                        <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: T.teal, background: T.tealDim, border: `1px solid ${T.tealGlow}`, borderRadius: 4, padding: '1px 6px' }}>{lead.tender_ref}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{lead.company_name}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', fontWeight: 600 }}>
                        {lead.opp_status === 'dead' || lead.opp_status === 'withdrawn' ? 'Dead' : 'Lost'}
                      </span>
                      {lead.estimated_value && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: T.surface2, color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: "'DM Mono', monospace" }}>
                          £{parseFloat(lead.estimated_value).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                        </span>
                      )}
                      {lead.assigned_bd && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: T.surface2, color: T.textMuted, border: `1px solid ${T.border}` }}>
                          {lead.assigned_bd.name}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: T.textDim, marginLeft: 'auto' }}>
                        {lead.lost_date ? new Date(lead.lost_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right: detail panel */}
              {lostSelected ? (
                <div style={{ flex: 1, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>{lostSelected.title}</div>
                      <div style={{ fontSize: 12, color: T.textMuted }}>{lostSelected.company_name}{lostSelected.tender_ref ? ` — ${lostSelected.tender_ref}` : ''}</div>
                    </div>
                    <button onClick={() => setLostSelected(null)} style={{ background: 'none', border: 'none', color: T.textDim, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                  </div>

                  {/* Loss narrative */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.textDim, marginBottom: 8 }}>Loss Narrative</div>
                    {lostSelected.lost_feedback ? (
                      <div style={{ fontSize: 12, color: T.text, background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 7, padding: '10px 12px', lineHeight: 1.6 }}>
                        {lostSelected.lost_feedback}
                      </div>
                    ) : lostSelected.lost_reason ? (
                      <div style={{ fontSize: 12, color: T.text, background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 7, padding: '10px 12px', lineHeight: 1.6 }}>
                        {lostSelected.lost_reason}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: T.textDim, fontStyle: 'italic' }}>No loss narrative recorded.</div>
                    )}
                  </div>

                  {/* BD Interaction history */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.textDim, marginBottom: 8 }}>
                      BD Interaction History ({lostSelected.interactions?.length || 0})
                    </div>
                    {(!lostSelected.interactions || lostSelected.interactions.length === 0) ? (
                      <div style={{ fontSize: 12, color: T.textDim, fontStyle: 'italic' }}>No interactions recorded.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {lostSelected.interactions.map((int, idx) => (
                          <div key={idx} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 7, padding: '8px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: int.notes ? 4 : 0 }}>
                              <span style={{
                                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase',
                                background: int.type === 'call' ? 'rgba(59,130,246,0.1)' : int.type === 'meeting' ? 'rgba(124,58,237,0.1)' : 'rgba(5,150,105,0.1)',
                                color: int.type === 'call' ? '#3b82f6' : int.type === 'meeting' ? '#7c3aed' : '#059669',
                              }}>{int.type}</span>
                              <span style={{ fontSize: 11, color: T.text, flex: 1 }}>{int.summary}</span>
                              <span style={{ fontSize: 10, color: T.textDim, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
                                {int.date ? new Date(int.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                              </span>
                            </div>
                            {int.notes && <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.5, paddingLeft: 2 }}>{int.notes}</div>}
                            <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Logged by {int.logged_by}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textDim, fontSize: 13, fontStyle: 'italic' }}>
                  Select a lead to view narrative and interaction history
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {viewMode === 'Companies' && <CompanyDirectory user={user} />}
      {viewMode === 'Contacts' && <ContactDirectory user={user} />}

      {['manager','director'].includes(user?.role) && lostEscalations.length > 0 && (
        <div style={{ margin: '0 0 14px', padding: '10px 16px', background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>
              {lostEscalations.length} lost lead{lostEscalations.length > 1 ? 's' : ''} missing feedback
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
              No lost reason captured after 10+ days:
            </span>
            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>
              {lostEscalations.slice(0,3).map(l => l.enq_ref || l.title).join(', ')}
              {lostEscalations.length > 3 ? ` +${lostEscalations.length - 3} more` : ''}
            </span>
          </div>
          <button onClick={() => setViewMode('List')}
            style={{ padding: '5px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            View in List →
          </button>
        </div>
      )}

      {viewMode === 'Pipeline' && <>
      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14, fontSize: 11, color: T.textMuted }}>
        <span>Bubble = estimated value</span>
        {[['< £50k', 16], ['£50k–£200k', 26], ['£200k–£500k', 36], ['> £500k', 46]].map(([label, size]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: size / 2, height: size / 2, borderRadius: '50%', background: T.tealDim, border: `2px solid ${T.tealGlow}` }} />
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* Board */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${STAGES.length}, 1fr)`, gap: 12, minWidth: 0 }}>
        {STAGES.map(stage => (
          <PipelineColumn key={stage.id} stage={stage} leads={byStage[stage.id] || []} user={user} onTenderCreated={onTenderCreated}
            onRefresh={load} onCardClick={setEditLead} onCRClick={setCrLead}
            onHover={(lead, pos) => setTooltip({ lead, pos })}
            onLeave={() => setTooltip({ lead: null, pos: null })} />
        ))}
      </div>

      </>}

      {tooltip.lead && <Tooltip lead={tooltip.lead} pos={tooltip.pos} />}
      {showNewLead && <NewLeadModal onClose={() => setShowNewLead(false)} onSaved={load} />}
      {editLead && <EditLeadModal lead={editLead} onClose={() => setEditLead(null)} onSaved={load} />}
    {crLead && (
      <ContractReviewModal
        tenderId={crLead.converted_opportunity_id}
        tenderName={crLead.title}
        onClose={() => setCrLead(null)}
        onComplete={() => { setCrLead(null); load(); }}
      />
    )}
    </div>
  );
}

